import { Box, Flex, Button, Card, Text } from '@opendoor/bricks/core';
import { ButtonVariant } from '@opendoor/bricks/system/theme';
import {
  getDurationInSeconds,
  parseDatetimeFromString,
  getApproxDuration,
} from '@opendoor/cinderblocks/helpers/datetime';
import { formatCurrency } from '@opendoor/bricks/helpers/currencyHelpers';
import { CONSUMER_URL } from 'components/globals';
import { useAuth } from '@opendoor/auth-fe';
import { useEffect, useState } from 'react';
import { getAthenaClient } from 'components/api';
import AgentExperiencePartnerCTA from './AgentExperiencePartnerCTA';

interface PropertyCtaProps {
  modifiedTimestamp?: string | null;
  listPrice?: number | null;
  addressId?: string | null;
  bwodTourable?: boolean | null;
  offerable?: boolean;
  buyerAgentPDP?: boolean | null;
  aepFirstName?: string | string[] | null;
  aepInitials?: (string | undefined)[] | undefined;
  aepEmail?: string | string[] | undefined;
  aepPhone?: string | string[] | null;
}

type OfferCtaFields = {
  ariaLabel: string;
  analyticsName: string;
  ctaText: string;
  ctaHref: string;
};

const useOfferCtaFields: (addressId: string | null | undefined) => OfferCtaFields = (addressId) => {
  const [ctaFields, setCtaFields] = useState<OfferCtaFields>({
    ariaLabel: 'Start Offer',
    analyticsName: 'cosmos-pdp-start-offer',
    ctaText: 'Start an offer',
    ctaHref: `${CONSUMER_URL}/dashboard/buy/offer?id=${addressId}`,
  });
  const { authentication } = useAuth();
  const isLoggedIn = authentication.state === 'authenticated';
  useEffect(() => {
    if (!isLoggedIn || !addressId) {
      return;
    }
    async function checkIfOfferExists() {
      if (!addressId) {
        return;
      }
      try {
        const olaId = (await getAthenaClient().GetOlaIdForAddress({ addressId })).buyer
          ?.getPropertyByAddressId?.property?.id;
        if (!olaId) {
          return;
        }
        const offers =
          (await getAthenaClient().GetOffersForCurrentUser()).buyer?.getOffersForCurrentUser
            ?.offers ?? [];
        const offer = offers.find((o) => o.propertyId === olaId);
        if (!offer) {
          return;
        }
        setCtaFields({
          ariaLabel: 'View Offer',
          analyticsName: 'cosmos-pdp-view-offer',
          ctaText: 'View your Offer',
          ctaHref: `${CONSUMER_URL}/dashboard/buy/offer/${offer.id}`,
        });
      } catch (e) {
        return;
      }
    }
    checkIfOfferExists().finally();
  }, [isLoggedIn, addressId]);
  return ctaFields;
};

export const DesktopPropertyCta: React.FC<
  PropertyCtaProps & {
    isCoverImagesInView: boolean;
  }
> = ({
  modifiedTimestamp,
  listPrice,
  addressId,
  isCoverImagesInView,
  bwodTourable,
  offerable,
  buyerAgentPDP,
  aepFirstName,
  aepInitials,
  aepEmail,
  aepPhone,
}) => {
  const duration = modifiedTimestamp
    ? getDurationInSeconds(parseDatetimeFromString(modifiedTimestamp) as Date, new Date())
    : undefined;
  const { ariaLabel, analyticsName, ctaText, ctaHref } = useOfferCtaFields(addressId);

  return (
    <Flex display={['none', 'none', 'none', 'block']} background="red" height="fit-content">
      <Flex position="relative">
        <Box position="absolute" width={buyerAgentPDP ? '389px' : '350px'} right={0}>
          <Card
            boxShadow="z2"
            borderRadius="roundedSmooth"
            id="pdp-offer-tour-action-card"
            analyticsName="cosmos-pdp-desktop-offer-card"
            width={buyerAgentPDP ? '389px' : '350px'}
            variant="primary"
            flex={1}
            justifyContent="center"
            textAlign="center"
            flexShrink={0}
            position="fixed"
            zIndex={50}
            top={isCoverImagesInView ? undefined : '100px'}
          >
            <Flex width="100%" flexDirection="column" alignItems="flex-start">
              <Text color="neutrals70" fontSize="s0" fontWeight="semibold">
                List price
              </Text>
              <Text color="neutrals100" fontSize="s4" fontWeight="bold" mb="3">
                {listPrice && formatCurrency(listPrice / 100)}
              </Text>
              {duration && !buyerAgentPDP && (
                <Text fontSize="s0" mb="4" color="neutrals70">
                  Updated {getApproxDuration(duration)} ago
                </Text>
              )}
              {buyerAgentPDP && (
                <AgentExperiencePartnerCTA
                  aepFirstName={aepFirstName}
                  aepInitials={aepInitials}
                  aepEmail={aepEmail}
                  aepPhone={aepPhone}
                />
              )}
              {offerable && !buyerAgentPDP && (
                <Button
                  aria-label={ariaLabel}
                  width="100%"
                  onClick={() => window.location.assign(ctaHref)}
                  variant="secondary"
                  analyticsName={analyticsName}
                  mb="4"
                >
                  {ctaText}
                </Button>
              )}
              {bwodTourable && !buyerAgentPDP && <StartTourCta addressId={addressId} />}
            </Flex>
          </Card>
        </Box>
      </Flex>
    </Flex>
  );
};

export const StartTourCta: React.FC<{
  buttonVariant?: ButtonVariant;
  addressId?: string | null;
}> = ({ buttonVariant = 'primary', addressId }) => {
  return (
    <Button
      aria-label="Take tour"
      width="100%"
      onClick={() => window.location.assign(`${CONSUMER_URL}/dashboard/buy/tour?id=${addressId}`)}
      variant={buttonVariant}
      analyticsName="cosmos-pdp-take-tour"
    >
      Take a tour
    </Button>
  );
};

export const MobilePropertyCta: React.FC<PropertyCtaProps> = ({
  listPrice,
  addressId,
  offerable,
  buyerAgentPDP,
}) => {
  const { ariaLabel, analyticsName, ctaText, ctaHref } = useOfferCtaFields(addressId);
  return (
    <Flex
      zIndex={50}
      display={['flex', 'flex', 'flex', 'none']}
      position="fixed"
      bottom={0}
      backgroundColor="neutrals0"
      width="100%"
      justifyContent="space-between"
      p={6}
      borderTopRadius="semiRounded"
      boxShadow="0 10px 22px rgb(29 71 123 / 8%), 0 2px 12px rgb(29 71 123 / 8%)"
    >
      <Box>
        <Flex flexDirection="column">
          <Text color="neutrals100" fontSize="s2" fontWeight="semibold" mb="1">
            {listPrice && formatCurrency(listPrice / 100)}
          </Text>
          <Text color="neutrals70" fontSize="s0">
            List price
          </Text>
        </Flex>
      </Box>
      {offerable && !buyerAgentPDP && (
        <Box>
          <Button
            aria-label={ariaLabel}
            onClick={() => window.location.assign(ctaHref)}
            variant="primary"
            analyticsName={analyticsName}
          >
            {ctaText}
          </Button>
        </Box>
      )}
      {buyerAgentPDP && (
        <Box>
          <Button
            aria-label="Schedule with ShowingTime"
            width="100%"
            onClick={() => window.open('https://www.showingtime.com/login', '_blank')}
            variant="primary"
            analyticsName="'cosmos-pdp-schedule-with-showingtime'"
          >
            Schedule
          </Button>
        </Box>
      )}
    </Flex>
  );
};
